import moment, { type Moment } from 'moment';
import { Formik } from 'formik';
import classnames from 'classnames';
import React, { useState } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import { HORIZONTAL_ORIENTATION } from 'react-dates/constants';

import history from 'utils/history';
import * as toast from 'utils/toast';

import { calendar } from 'assets/images';

import * as routes from 'constants/routes';

import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';
import DatePicker from 'components/common/datepicker/DatePicker';
import DeleteButton from 'components/common/button/DeleteButton';

import {
  useCreateReminderMutation,
  useDeleteReminderMutation,
  useUpdateReminderMutation,
} from 'hooks/query/useUserInfo';
import reminderSchema from 'schemas/reminders';

type ReminderFormValues = {
  title: string;
  redirectUrl: string | null;
  startDate: Moment;
  endDate: Moment;
};

type ReminderPayload = {
  title: string;
  redirectUrl: string | null;
  startDate: string;
  endDate: string;
};

type ReminderRecord = ReminderFormValues & { id: number };

const initialValues: ReminderFormValues = {
  title: '',
  redirectUrl: '',
  startDate: moment(),
  endDate: moment(),
};

interface Props {
  reminder: ReminderRecord | null;
}

const ReminderForm: React.FC<Props> = ({ reminder }) => {
  const [submitting, setSubmitting] = useState(false);
  const createReminderMutation = useCreateReminderMutation();
  const updateReminderMutation = useUpdateReminderMutation();
  const deleteReminderMutation = useDeleteReminderMutation();

  const handleCancelClick = () => (history.length > 1 ? history.goBack() : history.push(routes.REMINDERS));

  const handleSubmit = (values: ReminderFormValues) => {
    setSubmitting(true);

    const reminderObj: ReminderPayload = {
      title: values.title,
      redirectUrl: values.redirectUrl,
      endDate: values.endDate.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      startDate: values.startDate.clone().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };

    const onSuccess = () => {
      toast.success({
        title: 'Success',
        message: reminder ? 'Reminder updated successfully.' : 'Reminder created successfully.',
      });
      history.push(routes.REMINDERS);
    };

    const onSettled = () => setSubmitting(false);

    if (reminder) {
      updateReminderMutation.mutate({ id: reminder.id, reminder: reminderObj }, { onSuccess, onSettled });
    } else {
      createReminderMutation.mutate(reminderObj, { onSuccess, onSettled });
    }
  };

  const handleDeleteClick = () => {
    if (!reminder) {
      return;
    }

    deleteReminderMutation.mutate(reminder.id, {
      onSuccess: () => {
        toast.success({
          title: 'Deleted',
          message: 'Reminder has been deleted successfully.',
        });
        history.push(routes.REMINDERS);
      },
    });
  };

  return (
    <Formik<ReminderFormValues>
      initialValues={reminder ? { ...reminder } : { ...initialValues }}
      validationSchema={reminderSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: submitForm, values, handleBlur, handleChange, errors, touched, setFieldValue }) => {
        return (
          <form onSubmit={submitForm}>
            <div className="full-scope-card__content">
              <div className="form-wrap">
                <div className="form-wrap__row form-wrap__row--no-margin clearfix">
                  <div className="form-wrap__col col-sm-6 px-0">
                    <div className="form-group">
                      <label className="form-label dark--text">
                        Title <span className="required ml-5">*</span>
                      </label>
                      <input
                        name="title"
                        type="text"
                        value={values.title}
                        placeholder="Provide a suitable title for the reminder"
                        className="form-elem"
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      {typeof errors.title === 'string' && touched.title && (
                        <div className="error-block">{errors.title}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label dark--text">
                        Link <span className="required ml-5">*</span>
                      </label>
                      <input
                        name="redirectUrl"
                        type="text"
                        value={values.redirectUrl || ''}
                        placeholder="Provide a redirect link"
                        className="form-elem"
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      {typeof errors.redirectUrl === 'string' && touched.redirectUrl && (
                        <div className="error-block">{errors.redirectUrl}</div>
                      )}
                    </div>
                    <div className="d-flex flex-direction-responsive">
                      <div className="form-group width--full momr-20">
                        <label className="form-label dark--text">
                          Start Date <span className="required ml-5">*</span>
                        </label>
                        <div className="form-elem--icon-pos">
                          <DatePicker
                            id="startDate"
                            displayFormat="LL"
                            customInputIcon={<img className="form-icon" src={calendar} alt="Calendar" />}
                            date={values.startDate}
                            onDateChange={(date) => {
                              const nextDate = moment(date);
                              if (!nextDate.isValid()) {
                                return;
                              }
                              const endDate = nextDate.isAfter(values.endDate, 'day') ? nextDate : values.endDate;

                              setFieldValue('startDate', nextDate);
                              setFieldValue('endDate', endDate);
                            }}
                            numberOfMonths={1}
                            orientation={HORIZONTAL_ORIENTATION}
                            isOutsideRange={() => false}
                          />
                        </div>
                        {typeof errors.startDate === 'string' && touched.startDate && (
                          <div className="error-block">{errors.startDate}</div>
                        )}
                      </div>
                      <div className="form-group width--full">
                        <label className="form-label dark--text">
                          End Date <span className="required ml-5">*</span>
                        </label>
                        <div className="form-elem--icon-pos">
                          <DatePicker
                            id="endDate"
                            displayFormat="LL"
                            customInputIcon={<img className="form-icon" src={calendar} alt="Calendar" />}
                            date={values.endDate}
                            onDateChange={(date) => {
                              const nextDate = moment(date);
                              if (!nextDate.isValid()) {
                                return;
                              }
                              const startDate = nextDate.isBefore(values.startDate, 'day')
                                ? nextDate
                                : values.startDate;

                              setFieldValue('startDate', startDate);
                              setFieldValue('endDate', nextDate);
                            }}
                            numberOfMonths={1}
                            orientation={HORIZONTAL_ORIENTATION}
                          />
                        </div>
                        {typeof errors.endDate === 'string' && touched.endDate && (
                          <div className="error-block">{errors.endDate}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="action-bar-footer action-bar-footer--bordered-top px-16">
                <button
                  type="submit"
                  className={classnames('btn btn--primary btn--curved mr-10', {
                    'btn--loading': submitting,
                  })}
                  disabled={submitting}
                >
                  <span className={submitting ? 'invisible' : ''}>Save</span>
                  {submitting && <Loading />}
                </button>
                <button
                  type="button"
                  className={classnames('btn btn--outlined-grey btn--curved mr-10')}
                  disabled={submitting}
                  onClick={handleCancelClick}
                >
                  Cancel
                </button>
                {reminder && (
                  <LFModal
                    TriggerBtn={DeleteButton}
                    modalStatusIcon={<FiAlertCircle color="#F44336" size={24} />}
                    title={{ text: 'Delete this reminder?', type: 'danger' }}
                    className="delete-modal"
                    message={{ text: "You won't be able to revert this!", type: 'delete__text' }}
                    renderFooter={true}
                    buttons={[
                      {
                        text: 'Confirm',
                        type: 'red',
                        className: 'mr-12 delete__btn',
                        onClick: handleDeleteClick,
                        close: true,
                        showLoading: true,
                      },
                      {
                        text: 'Cancel',
                        type: 'outlined-grey',
                        className: 'py-8 px-12',
                        close: true,
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default ReminderForm;
